import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CardShell, asText, asNumber, fmt } from './shared';
import { evaluateFormula } from './formula';

interface NumberInput { id?: unknown; rotulo?: unknown; unidade?: unknown; padrao?: unknown; min?: unknown; max?: unknown; passo?: unknown }
interface ChoiceInput { id?: unknown; rotulo?: unknown; opcoes?: { rotulo?: unknown; valor?: unknown }[]; padrao?: unknown }

export interface CalculatorSpec {
  tipo: 'calculadora';
  titulo?: string;
  entradas?: (NumberInput | ChoiceInput)[];
  resultados?: { rotulo?: unknown; formula?: unknown; unidade?: unknown; casas?: unknown }[];
  aviso?: string;
  fonte?: string;
}

const isChoice = (e: any): e is ChoiceInput => Array.isArray(e?.opcoes) && e.opcoes.length > 0;

function Calculator({ spec }: { spec: CalculatorSpec }) {
  const inputs = (spec.entradas || []).filter((e: any) => e && asText(e.id));
  const results = (spec.resultados || []).filter((r) => r && asText(r.formula));
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(inputs.map((e: any) => {
    if (isChoice(e)) {
      const def = e.padrao !== undefined ? asText(e.padrao) : asText(e.opcoes![0].valor);
      return [asText(e.id), def];
    }
    return [asText(e.id), e.padrao !== undefined ? asText(e.padrao) : ''];
  })));

  const computed = useMemo(() => {
    const vars: Record<string, number> = {};
    let missing = false;
    for (const e of inputs as any[]) {
      const n = asNumber(values[asText(e.id)]);
      if (!Number.isFinite(n)) missing = true;
      vars[asText(e.id)] = n;
    }
    return results.map((r) => {
      if (missing) return { ok: false, text: '—' };
      try {
        const v = evaluateFormula(asText(r.formula), vars);
        const digits = r.casas !== undefined ? Math.max(0, Math.min(6, Number(r.casas))) : undefined;
        return { ok: Number.isFinite(v), text: fmt(v, digits) };
      } catch {
        return { ok: false, text: '—', error: true };
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, spec]);

  if (!inputs.length || !results.length) return null;

  return (
    <CardShell title={spec.titulo} source={spec.fonte} label="calculadora">
      <Layout>
        <Inputs>
          {inputs.map((e: any) => {
            const id = asText(e.id);
            return (
              <Field key={id}>
                <FieldLabel htmlFor={`genui-${id}`}>{asText(e.rotulo) || id}</FieldLabel>
                {isChoice(e) ? (
                  <Select id={`genui-${id}`} value={values[id]} onChange={(ev) => setValues((v) => ({ ...v, [id]: ev.target.value }))}>
                    {e.opcoes!.map((o, i) => <option key={i} value={asText(o.valor)}>{asText(o.rotulo)}</option>)}
                  </Select>
                ) : (
                  <InputWrap>
                    <NumberField
                      id={`genui-${id}`}
                      type="number"
                      inputMode="decimal"
                      value={values[id]}
                      min={e.min !== undefined ? Number(e.min) : undefined}
                      max={e.max !== undefined ? Number(e.max) : undefined}
                      step={e.passo !== undefined ? Number(e.passo) : 'any'}
                      onChange={(ev) => setValues((v) => ({ ...v, [id]: ev.target.value }))}
                    />
                    {e.unidade !== undefined && <Unit>{asText(e.unidade)}</Unit>}
                  </InputWrap>
                )}
              </Field>
            );
          })}
        </Inputs>
        <Results aria-live="polite">
          {results.map((r, i) => (
            <Result key={i}>
              <ResultLabel>{asText(r.rotulo)}</ResultLabel>
              <ResultValue $muted={!computed[i].ok}>
                {computed[i].text}
                {computed[i].ok && r.unidade !== undefined && <small> {asText(r.unidade)}</small>}
              </ResultValue>
            </Result>
          ))}
        </Results>
      </Layout>
      {spec.aviso && (
        <Notice><InfoOutlinedIcon style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }} /><span>{spec.aviso}</span></Notice>
      )}
    </CardShell>
  );
}

export default Calculator;

const accent = ({ theme }: any) => theme.prompt_accent_color || '#1a3c34';
const border = ({ theme }: any) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.15)');

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
  @media (max-width: 600px) { grid-template-columns: minmax(0, 1fr); }
`;

const Inputs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-size: 0.78rem;
  font-weight: 500;
  opacity: 0.8;
`;

const InputWrap = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${border};
  border-radius: 8px;
  padding: 0 10px;
  &:focus-within { border-color: ${accent}; }
`;

const NumberField = styled.input`
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.9rem;
  padding: 7px 0;
  font-variant-numeric: tabular-nums;
`;

const Unit = styled.span`
  font-size: 0.8rem;
  opacity: 0.6;
  margin-left: 6px;
`;

const Select = styled.select`
  border: 1px solid ${border};
  border-radius: 8px;
  padding: 7px 8px;
  font: inherit;
  font-size: 0.88rem;
  color: inherit;
  background: transparent;
  &:focus { outline: none; border-color: ${accent}; }
  option { color: #000; }
`;

const Results = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  padding: 12px 14px;
  border-radius: 10px;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(93,168,138,0.10)' : 'rgba(26,60,52,0.05)')};
`;

const Result = styled.div``;

const ResultLabel = styled.div`
  font-size: 0.78rem;
  opacity: 0.75;
`;

const ResultValue = styled.div<{ $muted: boolean }>`
  font-size: 1.35rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${({ $muted, theme }) => ($muted ? 'inherit' : accent({ theme }))};
  opacity: ${({ $muted }) => ($muted ? 0.4 : 1)};
  small { font-size: 0.8rem; font-weight: 500; opacity: 0.75; }
`;

const Notice = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  font-size: 0.78rem;
  line-height: 1.45;
  opacity: 0.75;
`;
